import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download, Eye, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SkeletonCard } from '../components/ui/skeleton';
import { useContributionReport, useFinancialReport, useLoanReport, useSummaryReport } from '../hooks/useQueries';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface ReportCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  lastGenerated?: Date;
  onView: () => void;
  onExport: (format: 'pdf' | 'excel' | 'csv') => void;
  isLoading?: boolean;
  error?: Error | null;
  requiresAuth?: boolean;
}

function ReportCard({ 
  title, 
  description, 
  icon, 
  lastGenerated, 
  onView, 
  onExport,
  isLoading,
  error,
  requiresAuth
}: ReportCardProps) {
  const { t } = useTranslation();
  const [exportLoading, setExportLoading] = useState(false);

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    setExportLoading(true);
    try {
      await onExport(format);
      toast.success(t('reports.exportSuccess', { format: format.toUpperCase() }));
    } catch (error) {
      toast.error(t('reports.exportError'));
    } finally {
      setExportLoading(false);
    }
  };

  if (isLoading) {
    return <SkeletonCard />;
  }

  if (error) {
    return (
      <Card className="h-full">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {icon}
              <CardTitle className="text-lg">{title}</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600">{t('reports.loadError')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              {requiresAuth && (
                <span className="text-xs text-orange-600 font-medium">
                  {t('reports.boardOnly')}
                </span>
              )}
            </div>
          </div>
        </div>
        <CardDescription className="mt-2">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {lastGenerated && (
          <p className="text-sm text-gray-500 mb-4">
            {t('reports.lastGenerated')}: {format(lastGenerated, 'dd/MM/yyyy HH:mm')}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            onClick={onView} 
            variant="outline" 
            className="flex-1"
          >
            <Eye className="h-4 w-4 mr-2" />
            {t('reports.view')}
          </Button>
          <div className="flex gap-2 flex-1">
            <Button
              size="sm"
              variant="default"
              onClick={() => handleExport('pdf')}
              disabled={exportLoading}
              className="flex-1"
            >
              <Download className="h-4 w-4" />
              PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleExport('excel')}
              disabled={exportLoading}
              className="flex-1"
            >
              <Download className="h-4 w-4" />
              Excel
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ReportsPage() {
  const { t } = useTranslation();
  
  // Fetch report data
  const { data: summaryData, error: summaryError, isLoading: summaryLoading } = useSummaryReport();
  const { data: contributionData, error: contributionError, isLoading: contributionLoading } = useContributionReport();
  const { data: loanData, error: loanError, isLoading: loanLoading } = useLoanReport();
  const { data: financialData, error: financialError, isLoading: financialLoading } = useFinancialReport();

  const handleViewReport = (reportType: string) => {
    // TODO: Implement report preview modal
    toast.info(t('reports.previewNotImplemented', { report: reportType }));
  };

  const handleExportReport = async (reportType: string, format: 'pdf' | 'excel' | 'csv') => {
    // TODO: Implement actual export functionality
    // For now, we'll simulate the export
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log(`Exporting ${reportType} report as ${format}`);
  };

  const reports = [
    {
      id: 'summary',
      title: t('reports.summary.title'),
      description: t('reports.summary.description'),
      icon: <BarChart3 className="h-5 w-5 text-blue-600" />,
      data: summaryData,
      error: summaryError,
      isLoading: summaryLoading,
      requiresAuth: false,
    },
    {
      id: 'contributions',
      title: t('reports.contributions.title'),
      description: t('reports.contributions.description'),
      icon: <TrendingUp className="h-5 w-5 text-emerald-600" />,
      data: contributionData,
      error: contributionError,
      isLoading: contributionLoading,
      requiresAuth: false,
    },
    {
      id: 'loans',
      title: t('reports.loans.title'),
      description: t('reports.loans.description'),
      icon: <DollarSign className="h-5 w-5 text-orange-600" />,
      data: loanData,
      error: loanError,
      isLoading: loanLoading,
      requiresAuth: false,
    },
    {
      id: 'financial',
      title: t('reports.financial.title'),
      description: t('reports.financial.description'),
      icon: <FileText className="h-5 w-5 text-purple-600" />,
      data: financialData,
      error: financialError,
      isLoading: financialLoading,
      requiresAuth: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
        <p className="mt-2 text-gray-600">{t('reports.subtitle')}</p>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reports.map((report) => (
          <ReportCard
            key={report.id}
            title={report.title}
            description={report.description}
            icon={report.icon}
            lastGenerated={report.data ? new Date() : undefined}
            onView={() => handleViewReport(report.id)}
            onExport={(format) => handleExportReport(report.id, format)}
            isLoading={report.isLoading}
            error={report.error}
            requiresAuth={report.requiresAuth}
          />
        ))}
      </div>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('reports.info.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
            <li>{t('reports.info.item1')}</li>
            <li>{t('reports.info.item2')}</li>
            <li>{t('reports.info.item3')}</li>
            <li>{t('reports.info.item4')}</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}